import { useEffect, useState, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import Select, { components } from "react-select";
import type { StylesConfig } from "react-select";
import { GET_TRUSTEE } from "../../Querries";
import { CustomDropdownIndicator } from "../../pages/SchoolList";

interface TrusteeOption {
  label: string;
  value: string;
  name: string;
}

function TrusteeFilter({ value, onChange, className = "", menuIsOpen }: any) {
  const [limit, setLimit] = useState(10);
  const [trustees, setTrustees] = useState<TrusteeOption[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState("");

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data, loading, refetch } = useQuery(GET_TRUSTEE, {
    variables: { page: 1, limit: limit, search: inputValue },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (data?.getAllTrustee?.data) {
      const fetchedTrustees = data.getAllTrustee.data.map((trustee: any) => ({
        label: `${trustee.name} (${trustee._id})`,
        value: trustee._id,
        name: trustee.name,
      }));
      setTrustees(fetchedTrustees);
      setTotalCount(data.getAllTrustee.totalItems);
    }
  }, [data]);

  const handleScroll = (event: any) => {
    const target = event.target as HTMLDivElement;
    if (
      !loading &&
      totalCount !== null &&
      trustees.length < totalCount &&
      target.scrollHeight - target.scrollTop === target.clientHeight
    ) {
      setLimit((prev) => Math.min(prev + 10, totalCount));
    }
  };

  const debouncedSearch = useCallback(
    (val: string) => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(async () => {
        setInputValue(val);
        const res = await refetch({ search: val, page: 1, limit: limit });
        if (res?.data?.getAllTrustee?.data) {
          const updatedtrustee = res.data.getAllTrustee.data.map(
            (trustee: any) => ({
              label: `${trustee.name} (${trustee._id})`,
              value: trustee._id,
              name: trustee.name,
            })
          );
          setTrustees(updatedtrustee);
          setTotalCount(res.data.getAllTrustee.totalItems);
        }
      }, 700);
    },
    [limit, refetch]
  );

  const CustomMenuList = (props: any) => {
    const { children } = props;
    return (
      <components.MenuList {...props}>
        <div>
          {children}
          {/* {loading && <div className="py-2 text-center text-sm">Loading…</div>} */}
          {totalCount !== null && trustees.length >= totalCount && (
            <div className="py-2 text-center text-xs text-gray-500">
              All content loaded.
            </div>
          )}
          {totalCount !== null && trustees.length < totalCount && !loading && (
            <div className="py-2 text-center text-xs text-gray-500">
              Scroll down to load more...
            </div>
          )}
        </div>
      </components.MenuList>
    );
  };

  const customStyles: StylesConfig<TrusteeOption> = {
    control: (base) => ({
      ...base,
      backgroundColor: "#F6F8FA",
      border: "1px solid #1B163B",
      borderRadius: "6px",
      minHeight: "15px",
      margin: 0,
      fontSize: "14px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0px 0.5rem",
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "200px",
    }),
    input: (base) => ({
      ...base,
      backgroundColor: "transparent",
      color: "#000",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#1E1B59",
      fontSize: "12px",
    }),
  };

  return (
    <div className="w-full flex flex-col">
      <Select
        className="font-normal capitalize"
        closeMenuOnSelect={true}
        onMenuScrollToBottom={handleScroll}
        onInputChange={(val) => debouncedSearch(val)}
        options={trustees}
        menuIsOpen={menuIsOpen}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: () => null,
          MenuList: CustomMenuList,
        }}
        onChange={(selected: any) => {
          onChange(selected);
        }}
        placeholder="Select Trustee"
        styles={customStyles}
        isMulti={false}
        isLoading={loading}
        value={value}
      />
    </div>
  );
}

export default TrusteeFilter;
